import { db } from '../config/firebase';
import { collection, addDoc, updateDoc, deleteDoc, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { IPOS_DISHES_COLLECTION } from '../constants/cukcuk';
import { Dish } from '../types/dish';
import { Branch2Dish } from '../types/branch2Dish';

export class DishService {
  private dishes: Dish[] = [];

  constructor(private readonly collectionName: string = IPOS_DISHES_COLLECTION) {}

  getCollectionName(): string {
    return this.collectionName;
  }

  async addDish(dish: Dish): Promise<Dish> {
    try {
      const { id: _id, ...dishData } = dish;
      const payload = this.stripUndefined({
        ...dishData,
        createdAt: new Date(),
        updatedAt: new Date(),
      });
      const docRef = await addDoc(collection(db, this.collectionName), payload);
      return { id: docRef.id, ...dishData };
    } catch (error) {
      throw new Error('Cannot add dish: ' + error);
    }
  }

  async getAllDishes(): Promise<Dish[]> {
    try {
      // Get all dishes without ordering first
      const querySnapshot = await getDocs(collection(db, this.collectionName));
      
      // Convert to Dish objects
      const dishes = querySnapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      })) as Dish[];
      
      // Sort dishes by code to match Excel order
      // Excel typically sorts by the code column which is usually sequential
      return dishes.sort((a, b) => {
        // First try to sort by displayOrder if it exists
        if (a.displayOrder !== undefined && b.displayOrder !== undefined) {
          return a.displayOrder - b.displayOrder;
        }
        
        // If no displayOrder, sort by code
        if (a.code && b.code) {
          // Extract numbers from codes if they exist (e.g., ITEM_001 -> 001)
          const aMatch = a.code.match(/\d+/);
          const bMatch = b.code.match(/\d+/);
          
          if (aMatch && bMatch) {
            return parseInt(aMatch[0]) - parseInt(bMatch[0]);
          }
          
          // If no numbers, sort alphabetically
          return a.code.localeCompare(b.code);
        }
        
        return 0;
      });
    } catch (error) {
      throw new Error('Cannot retrieve dishes: ' + error);
    }
  }

  async getDishById(id: string): Promise<Dish | null> {
    const docRef = doc(db, this.collectionName, id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as Dish;
    }
    return null;
  }

  async updateDish(id: string, dish: Partial<Dish>): Promise<void> {
    try {
      const docRef = doc(db, this.collectionName, id);
      const { id: _id, ...dishData } = dish;
      await updateDoc(
        docRef,
        this.stripUndefined({
          ...dishData,
          updatedAt: new Date(),
        }),
      );
    } catch (error) {
      throw new Error('Cannot update dish: ' + error);
    }
  }

  async deleteDish(id: string): Promise<void> {
    try {
      const docRef = doc(db, this.collectionName, id);
      await deleteDoc(docRef);
    } catch (error) {
      throw new Error('Cannot delete dish: ' + error);
    }
  }

  async getBranch2Dishes(): Promise<Branch2Dish[]> {
    try {
      const q = query(collection(db, this.collectionName), where('branch', '==', 'branch2'));
      const snapshot = await getDocs(q);
      return snapshot.docs
        .map((d) => ({ id: d.id, ...d.data() } as Branch2Dish))
        .sort((a, b) => a.name.localeCompare(b.name, 'vi'));
    } catch (error) {
      throw new Error('Cannot retrieve branch2 dishes: ' + error);
    }
  }

  private stripUndefined<T extends Record<string, any>>(obj: T): T {
    const result = {} as any;
    for (const key of Object.keys(obj)) {
      if (obj[key] !== undefined) {
        result[key] = obj[key];
      }
    }
    return result;
  }

  async addBranch2Dish(dish: Omit<Branch2Dish, 'id'>): Promise<Branch2Dish> {
    try {
      const payload = this.stripUndefined({
        ...dish,
        branch: 'branch2' as const,
        isActive: dish.isActive ?? true,
        createdAt: new Date(),
        updatedAt: new Date(),
      });
      const docRef = await addDoc(collection(db, this.collectionName), payload);
      return { id: docRef.id, ...payload };
    } catch (error) {
      throw new Error('Cannot add branch2 dish: ' + error);
    }
  }

  async updateBranch2Dish(id: string, dish: Partial<Branch2Dish>): Promise<void> {
    try {
      const docRef = doc(db, this.collectionName, id);
      const cleaned = this.stripUndefined({ ...dish, updatedAt: new Date() });
      await updateDoc(docRef, cleaned);
    } catch (error) {
      throw new Error('Cannot update branch2 dish: ' + error);
    }
  }

  async deleteBranch2Dish(id: string): Promise<void> {
    try {
      const docRef = doc(db, this.collectionName, id);
      await deleteDoc(docRef);
    } catch (error) {
      throw new Error('Cannot delete branch2 dish: ' + error);
    }
  }

  async batchUpdateFromExcel(dishes: Dish[]): Promise<{ added: number, updated: number, errors: string[] }> {
    const result: { added: number, updated: number, errors: string[] } = { 
      added: 0, 
      updated: 0, 
      errors: [] 
    };
    
    console.log('Starting batch update with dishes:', dishes);
    
    try {
      // Get all existing dishes to check for updates
      const existingDishes = await this.getAllDishes();
      console.log('Existing dishes count:', existingDishes.length);
      const existingDishMap = new Map<string, Dish>();
      
      // Create a map of existing dishes by code for quick lookup
      existingDishes.forEach(dish => {
        if (dish.code) {
          existingDishMap.set(dish.code, dish);
        }
      });
      console.log('Existing dish codes:', Array.from(existingDishMap.keys()));
      
      // Process each dish from Excel
      for (const dish of dishes) {
        try {
          console.log('Processing dish:', dish.name, 'with code:', dish.code);
          
          // Check if dish with this code already exists
          const existingDish = existingDishMap.get(dish.code);
          
          if (existingDish) {
            console.log('Updating existing dish:', existingDish.id);
            // Update existing dish
            await this.updateDish(existingDish.id!, dish);
            result.updated++;
          } else {
            console.log('Adding new dish');
            // Add new dish
            await this.addDish(dish);
            result.added++;
          }
        } catch (error) {
          console.error('Error processing dish:', error);
          const errorMessage = error instanceof Error ? error.message : String(error);
          result.errors.push(`Error processing dish ${dish.name} (${dish.code}): ${errorMessage}`);
        }
      }
      
      console.log('Batch update completed with result:', result);
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      throw new Error('Error in batch update: ' + errorMessage);
    }
  }
}